import { AnyAction } from 'redux';
import apis from '../../../utils/apis';
import {
  FETCH_TEAMS_REQUEST,
  FETCH_TEAMS_SUCCESS,
  FETCH_TEAMS_FAILURE,
} from '../../types';
import { showFeedbackModal } from '../UserFeedbackActions';
import { handleApiError } from '../utils/utils';
import { ThunkAction } from 'redux-thunk';
import { RootState } from '../..';
import { decodeHtmlEntities } from '../../../utils/textUtils';

export const fetchAllTeams = (params: {
  searchTerm?: string;
  filters?: { [key: string]: string[] };
  sortBy?: string;
  sortOrder?: string;
  page?: number;
  limit?: number;
} = {}): ThunkAction<Promise<any>, RootState, unknown, AnyAction> => async (dispatch) => {
  dispatch({ type: FETCH_TEAMS_REQUEST });
  try {
    const response = await apis.get('/org-people/teams');

    if (response && response.status === 200 && response.data?.success) {
      const teams = (response.data.teams || []).map((team: any) => ({
        ...team,
        name: decodeHtmlEntities(team.name),
        description: decodeHtmlEntities(team.description),
      }));
      
      dispatch({
        type: FETCH_TEAMS_SUCCESS,
        payload: {
          teams,
          total: teams.length,
          currentPage: 1,
          totalPages: 1,
        },
      });
      return teams;
    } else {
      throw new Error('Invalid response format');
    }
  } catch (error: any) {
    dispatch({
      type: FETCH_TEAMS_FAILURE,
      payload: error?.response?.data?.message || 'Failed to fetch teams',
    });
    handleApiError(error, dispatch);
    return [];
  }
};

export const createTeam = (teamData: { name: string; description?: string; practice_area_ids?: string[]; department_id?: string; members?: any[] }): ThunkAction<Promise<any>, RootState, unknown, AnyAction> => async (dispatch) => {
  try {
    const response = await apis.post('/org-people/teams', teamData);

    if (response?.data?.success || response?.status === 201 || response?.status === 200) {
      const team = response.data.team || response.data;
      
      dispatch(
        showFeedbackModal({
          showModal: true,
          message: `Team "${teamData.name}" created successfully`,
          modalType: 'success',
          duration: 3000,
        })
      );
      return team;
    } else {
      throw new Error('Failed to create team');
    }
  } catch (error: any) {
    const errorMsg = error?.response?.data?.message || 'Failed to create team';
    dispatch(
      showFeedbackModal({
        showModal: true,
        message: errorMsg,
        modalType: 'error',
        duration: 3000,
      })
    );
    return null;
  }
};

export const fetchTeamDetails = (teamId: string): ThunkAction<Promise<any>, RootState, unknown, AnyAction> => async (dispatch) => {
  try {
    const response = await apis.get(`/org-people/teams/${teamId}`);

    if (response?.data?.success || response?.status === 200) {
      const team = response.data.team || response.data;
      return team;
    } else {
      throw new Error('Failed to fetch team details');
    }
  } catch (error: any) {
    dispatch(
      showFeedbackModal({
        showModal: true,
        message: error?.response?.data?.message || 'Failed to load team',
        modalType: 'error',
        duration: 3000,
      })
    );
    return null;
  }
};

export const updateTeam = (teamId: string, teamData: { name?: string; description?: string; members?: string[]; practice_area_ids?: number[] }): ThunkAction<Promise<any>, RootState, unknown, AnyAction> => async (dispatch) => {
  try {

    const response = await apis.put(`/org-people/teams/${teamId}`, teamData);

    if (response?.data?.success && response?.status === 200) {
      const team = response.data.team || response.data;

      const updatedFields = [];
      if (teamData.name) updatedFields.push('name');
      if (teamData.description) updatedFields.push('description');
      if (teamData.members?.length) updatedFields.push('members');

      // dispatch(
      //   showFeedbackModal({
      //     showModal: true,
      //     message: updatedFields.length > 0 ? `Team updated successfully (${updatedFields.join(', ')})` : 'Team updated successfully',
      //     modalType: 'success',
      //     duration: 2000,
      //   })
      // );
      return team;
    } else if (response?.status === 400) {
      throw new Error(response?.data?.error || 'No fields to update');
    } else {
      throw new Error(response?.data?.error || 'Failed to update team');
    }
  } catch (error: any) {
    const errorMessage = error?.response?.data?.error || error?.response?.data?.message || error.message || 'Failed to update team';
    dispatch(
      showFeedbackModal({
        showModal: true,
        message: errorMessage,
        modalType: 'error',
        duration: 3000,
      })
    );
    throw error;
  }
};

export const deleteTeam = (teamId: string): ThunkAction<Promise<any>, RootState, unknown, AnyAction> => async (dispatch) => {
  try {
    const response = await apis.delete(`/org-people/teams/${teamId}`);

    if (response?.data?.success || response?.status === 200) {
      
      dispatch(
        showFeedbackModal({
          showModal: true,
          message: 'Team deleted successfully',
          modalType: 'success',
          duration: 2000,
        })
      );
      return true;
    } else {
      throw new Error('Failed to delete team');
    }
  } catch (error: any) {
    dispatch(
      showFeedbackModal({
        showModal: true,
        message: error?.response?.data?.message || 'Failed to delete team',
        modalType: 'error',
        duration: 3000,
      })
    );
    return false;
  }
};

export const addTeamMember = (teamId: string, memberId: string, role: string = 'member'): ThunkAction<Promise<any>, RootState, unknown, AnyAction> => async (dispatch) => {
  try {
    
    const response = await apis.post(`/org-people/teams/${teamId}/members`, {
      user_id: memberId,
      role,
    });

    if (response?.data?.success || response?.status === 200 || response?.status === 201) {
      const member = response.data.member || response.data;
      return member;
    } else {
      throw new Error('Failed to add member');
    }
  } catch (error: any) {
    dispatch(
      showFeedbackModal({
        showModal: true,
        message: error?.response?.data?.message || 'Failed to add member',
        modalType: 'error',
        duration: 3000,
      })
    );
    return null;
  }
};

export const removeTeamMember = (teamId: string, memberId: string): ThunkAction<Promise<any>, RootState, unknown, AnyAction> => async (dispatch) => {
  try {
    
    const response = await apis.delete(`/org-people/teams/${teamId}/members/${memberId}`);

    if (response?.data?.success || response?.status === 200) {
      return true;
    } else {
      throw new Error('Failed to remove member');
    }
  } catch (error: any) {
    dispatch(
      showFeedbackModal({
        showModal: true,
        message: error?.response?.data?.message || 'Failed to remove member',
        modalType: 'error',
        duration: 3000,
      })
    );
    return false;
  }
};

export const fetchTeamFeed = (teamId: string): ThunkAction<Promise<any>, RootState, unknown, AnyAction> => async (dispatch) => {
  try {
    const response = await apis.get(`/org-people/teams/${teamId}/feed`);

    if (response?.data?.success || response?.status === 200) {
      const feed = response.data.feed || [];
      return feed;
    } else {
      throw new Error('Failed to fetch feed');
    }
  } catch (error: any) {
    dispatch(
      showFeedbackModal({
        showModal: true,
        message: error?.response?.data?.message || 'Failed to load feed',
        modalType: 'error',
        duration: 3000,
      })
    );
    return [];
  }
};

export const postTeamFeedEvent = (teamId: string, eventData: { event_type: string; message: string; metadata?: any }): ThunkAction<Promise<any>, RootState, unknown, AnyAction> => async (dispatch) => {
  try {
    const response = await apis.post(`/org-people/teams/${teamId}/feed`, eventData);

    if (response?.data?.success || response?.status === 200 || response?.status === 201) {
      const event = response.data.event || response.data;

      return event;
    } else {
      throw new Error('Failed to post event');
    }
  } catch (error: any) {
    dispatch(
      showFeedbackModal({
        showModal: true,
        message: error?.response?.data?.message || 'Failed to post event',
        modalType: 'error',
        duration: 3000,
      })
    );
    return null;
  }
};

/**
 * View a document from a team (display inline)
 * Implements streaming of encrypted file content
 * Uses REST API endpoint: GET /org-people/teams/{team_uuid}/documents/{document_id}/view
 * Returns blob and metadata for displaying in a viewer
 */
export const viewTeamDocument = (
  teamId: string,
  documentId: string,
  filename: string
): ThunkAction<Promise<{ success: boolean; blob?: Blob; contentType?: string; filename?: string; error?: string }>, RootState, unknown, AnyAction> =>
  async (dispatch) => {
    try {
      const response = await apis({
        method: "GET",
        url: `/org-people/teams/${teamId}/documents/${documentId}/view`,
        responseType: 'blob'
      });

      if (!response) {
        console.error('[viewTeamDocument] No response received from server');
        return {
          success: false,
          error: 'No response from server'
        };
      }

      if (response.status !== 200) {
        return {
          success: false,
          error: `Server returned status ${response.status}`
        };
      }

      const blob = response.data;

      if (!blob || !(blob instanceof Blob)) {
        return {
          success: false,
          error: 'Invalid file content received from server'
        };
      }

      if (blob.size === 0) {
        return {
          success: false,
          error: 'File is empty or could not be retrieved'
        };
      }

      if (blob.type === 'text/html' || blob.type === '') {
        return {
          success: false,
          error: 'Server returned an error. File may not exist or access was denied.'
        };
      }

      const contentType = (response.headers['content-type'] || '').toLowerCase().trim() || 'application/octet-stream';

      let viewFilename = filename;
      const disposition = response.headers['content-disposition'];
      if (disposition) {
        const filenameMatch = disposition.match(/filename[^;=\n]*=(?:(['"]).*?\1|[^;\n]*)/);
        if (filenameMatch) {
          viewFilename = filenameMatch[1]?.replace(/['"]/g, '') || filename;
        }
      }

      return {
        success: true,
        blob,
        contentType,
        filename: viewFilename
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Error viewing document'
      };
    }
  };

/**
 * Trigger browser download of a blob with specified filename
 * Cross-browser compatible helper function
 */
function triggerBlobDownload(blob: Blob, filename: string): void {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

/**
 * Download a document from a team with streaming support
 * Uses REST API endpoint: GET /org-people/teams/{team_uuid}/documents/{document_id}/download
 * Returns blob that is saved to disk
 */
export const downloadTeamDocument = (teamId: string, documentId: string, filename: string): ThunkAction<Promise<boolean>, RootState, unknown, AnyAction> =>
  async (dispatch) => {
    try {
      const response = await apis({
        method: "GET",
        url: `/org-people/teams/${teamId}/documents/${documentId}/download`,
        responseType: 'blob'
      });

      if (!response) {
        dispatch(showFeedbackModal({
          modalType: 'error',
          message: 'No response from server',
          showModal: true,
          duration: 3000
        }));
        return false;
      }

      if (response.status !== 200) {
        dispatch(showFeedbackModal({
          modalType: 'error',
          message: `Server returned status ${response.status}`,
          showModal: true,
          duration: 3000
        }));
        return false;
      }

      const blob = response.data;

      if (!blob || !(blob instanceof Blob)) {
        dispatch(showFeedbackModal({
          modalType: 'error',
          message: 'Invalid file content received from server',
          showModal: true,
          duration: 3000
        }));
        return false;
      }

      if (blob.size === 0) {
        dispatch(showFeedbackModal({
          modalType: 'error',
          message: 'File is empty or could not be retrieved',
          showModal: true,
          duration: 3000
        }));
        return false;
      }

      if (blob.type === 'text/html' || blob.type === '') {
        dispatch(showFeedbackModal({
          modalType: 'error',
          message: 'Server returned an error. File may not exist or access was denied.',
          showModal: true,
          duration: 3000
        }));
        return false;
      }

      const disposition = response.headers['content-disposition'];
      let downloadFilename = filename;
      if (disposition) {
        const filenameMatch = disposition.match(/filename[^;=\n]*=(?:(['"]).*?\1|[^;\n]*)/);
        if (filenameMatch) {
          downloadFilename = filenameMatch[1]?.replace(/['"]/g, '') || filename;
        }
      }

      triggerBlobDownload(blob, downloadFilename);
      return true;
    } catch (error: any) {
      dispatch(showFeedbackModal({
        modalType: 'error',
        message: error.message || 'Error downloading document',
        showModal: true,
        duration: 3000
      }));
      return false;
    }
  };
